import type { FormFieldMapping } from '../types/fieldMapping';
import type { FormSpacerEntry, FormSectionDividerEntry, FormInlineEachEntry, FormFieldStackEntry } from '../types/layout';
import { extractConditionFieldPaths } from './extractConditionFieldPaths';

interface InlineActionButtonEntry {
  type: 'inline-action-button';
  actionId: string;
  hiddenWhen?: unknown;
  disabledWhen?: unknown;
}

type FieldEntry = FormFieldMapping | FormSpacerEntry | FormSectionDividerEntry | FormInlineEachEntry | FormFieldStackEntry | InlineActionButtonEntry;

function prefixPath(path: string, namePrefix?: string): string {
  return namePrefix ? `${namePrefix}.${path}` : path;
}

function addConditionPaths(
  paths: Set<string>,
  condition: Parameters<typeof extractConditionFieldPaths>[0],
  namePrefix?: string,
): void {
  for (const p of extractConditionFieldPaths(condition)) {
    paths.add(prefixPath(p, namePrefix));
  }
}

/**
 * Collects RHF watch paths for a single field's declarative state (when + legacy + reactions).
 */
export function extractFieldStateWatchPaths(field: FormFieldMapping, namePrefix?: string): string[] {
  const paths = new Set<string>();

  addConditionPaths(paths, field.when?.visible, namePrefix);
  addConditionPaths(paths, field.when?.required, namePrefix);
  addConditionPaths(paths, field.when?.disabled, namePrefix);
  addConditionPaths(paths, field.when?.readOnly, namePrefix);
  addConditionPaths(paths, field.when?.adapterHint?.condition, namePrefix);
  addConditionPaths(paths, field.when?.formatter?.condition, namePrefix);

  addConditionPaths(paths, field.conditionalVisible, namePrefix);
  addConditionPaths(paths, field.conditionalRequired, namePrefix);
  addConditionPaths(paths, field.conditionalDisabled, namePrefix);
  addConditionPaths(paths, field.conditionalAdapterHint?.condition, namePrefix);

  if (field.reactions?.length) {
    for (const reaction of field.reactions) {
      const watches = Array.isArray(reaction.watch) ? reaction.watch : [reaction.watch];
      watches.forEach((w) => paths.add(prefixPath(w, namePrefix)));
    }
  }

  return Array.from(paths);
}

/**
 * Collects watch paths across all fields in a form-fields node (for matrix visibility).
 */
export function extractMatrixVisibilityWatchPaths(
  fields: readonly (FieldEntry | { type: string;[key: string]: unknown })[],
  namePrefix?: string,
): string[] {
  const paths = new Set<string>();

  fields.forEach((entry) => {
    if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-each' || entry.type === 'stack')) return;
    if ('type' in entry && entry.type === 'inline-action-button') {
      const btn = entry as { hiddenWhen?: unknown; disabledWhen?: unknown };
      addConditionPaths(paths, btn.hiddenWhen as Parameters<typeof extractConditionFieldPaths>[0], namePrefix);
      addConditionPaths(paths, btn.disabledWhen as Parameters<typeof extractConditionFieldPaths>[0], namePrefix);
      return;
    }
    for (const p of extractFieldStateWatchPaths(entry as FormFieldMapping, namePrefix)) {
      paths.add(p);
    }
  });

  return Array.from(paths);
}
